import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SearchResult {
  id: string;
  username: string | null;
  name: string | null;
  avatar_url: string | null;
  is_active: boolean | null;
}

export const useUserSearch = (currentUserId?: string | null) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Debounced search effect
  useEffect(() => {
    const searchUsers = async () => {
      // Clear results if search term is too short
      if (searchTerm.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Use safe_users view (excludes sensitive data like email, stripe_account_id)
        let query = supabase
          .from('safe_users')
          .select('id, username, name, avatar_url, is_active')
          .not('username', 'is', null)
          .ilike('username', `%${searchTerm}%`);
        
        // Exclude current user
        if (currentUserId) {
          query = query.neq('id', currentUserId);
        }
        
        const { data, error: queryError } = await query
          .order('is_active', { ascending: false })
          .order('username', { ascending: true })
          .limit(10);
          
        if (queryError) throw queryError;
        
        setResults(data || []);
      } catch (err: any) {
        console.error("User search error:", err);
        setError(err.message || "Failed to search users");
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, currentUserId]);
  
  return { searchTerm, setSearchTerm, results, loading, error };
};
